import { Filetype } from "idfweb-vfs";

import type { RuntimeLink } from "./runtime.ts";

/**
 * Every file under the searched roots, for quick open and find in files. The
 * list is read once and dropped when the runtime worker reports a change in a
 * directory, so a new file shows up at the next search. Excluded directories
 * (the build tree, `.git`) are not listed at all, as VS Code leaves out what
 * `.gitignore` lists.
 */
export class FileIndex {
    readonly #runtime: RuntimeLink;
    #roots: string[] = [];
    #excluded: string[] = [];
    #files: Promise<string[]> | undefined;

    constructor(runtime: RuntimeLink) {
        this.#runtime = runtime;
    }

    configure(roots: string[], excluded: string[]): void {
        this.#roots = roots;
        this.#excluded = excluded;
        this.#files = undefined;
    }

    invalidate(): void {
        this.#files = undefined;
    }

    files(): Promise<string[]> {
        this.#files ??= this.#list().catch((e: unknown) => {
            this.#files = undefined;
            throw e;
        });
        return this.#files;
    }

    async #list(): Promise<string[]> {
        const files: string[] = [];
        const walk = async (dir: string): Promise<void> => {
            let entries;
            try {
                entries = await this.#runtime.fs.readdir(dir);
            } catch {
                return;
            }
            const below: Promise<void>[] = [];
            for (const entry of entries) {
                const path = `${dir}/${entry.name}`;
                if (entry.filetype === Filetype.DIRECTORY) {
                    if (entry.name === ".git" || this.#excluded.includes(path)) continue;
                    below.push(walk(path));
                } else files.push(path);
            }
            await Promise.all(below);
        };
        await Promise.all(this.#roots.map(walk));
        return files.sort();
    }
}
